import SVG from "react-inlinesvg";
import classnames from "classnames";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import { useEffect } from "react";
import { Trans, useTranslation, withTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";

import { useFormatCurrency } from "@krea/common/hooks";
import BankIdSVG from "@krea/common/images/svg/bankid/bankid-colorless.svg";
import { settings } from "@krea/common/settings";
import Button from "@krea/common/shared-components/button";
import ClipboardCopyLink from "@krea/common/shared-components/clipboard-copy-link";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@krea/common/shared-components/dialog";
import { REASONS } from "@krea/common/shared-components/loan-application-form/fields/reason";
import { Text } from "@krea/common/shared-components/text";
import { Toast } from "@krea/common/shared-components/toast/Toast";
import { useBankIDStore } from "@krea/common/store/bankID/hooks";
import { useLoanApplicationFormStore } from "@krea/common/store/loan-application-form/hooks";
import { useModalStore } from "@krea/common/store/modal/hooks";
import { COUNTRY_CODE } from "@krea/common/utils";

import styles from "./RemoteIdentificationModalSMS.module.scss";
import SMSFlow from "./SMSFlow";

const RemoteIdentificationModalSMS = ({
  isPending,
  isError,
  cancelApplicationViaBankID,
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [formattedCurrencyValue] = useFormatCurrency({
    currency: settings.currency,
  });

  const resetBankIDStore = useBankIDStore((state) => state.resetBankIDStore);
  const closeModal = useModalStore(({ closeModal }) => closeModal);

  const {
    reason,
    requestedLoanAmount,
    organisation,
    remoteIdentification,
    formErrors,
    clearRemoteIdentification,
  } = useLoanApplicationFormStore(
    useShallow((state) => ({
      reason: state.reason,
      requestedLoanAmount: state.requestedLoanAmount,
      organisation: state.organisation,
      remoteIdentification: state.remoteIdentification,
      formErrors: state.formErrors,
      clearRemoteIdentification: state.clearRemoteIdentification,
    })),
  );

  const organisationName = organisation.name;
  const {
    URL: remoteIdentificationURL,
    URLExpiryDate: remoteIdentificationURLExpiryDate,
  } = remoteIdentification;

  const isSweden = settings.countryCode === COUNTRY_CODE.SE;
  const modalTitle = isSweden
    ? t("remoteIdentification.SMSIdentifyKrea")
    : t("remoteIdentification.SMSIdentifyKredita");
  const expiryDate = dayjs(remoteIdentificationURLExpiryDate);
  const expiryDateStr = expiryDate.isValid()
    ? expiryDate.format("DD/MM/YYYY")
    : null;

  const getReason = () => {
    return REASONS.find(({ value }) => value === reason)?.name;
  };

  const onCloseButtonClick = () => {
    cancelApplicationViaBankID({ isSMSFlow: true });
    resetBankIDStore();
    closeModal();
    clearRemoteIdentification();
  };

  const content = [
    {
      label: t("applicationForm.commons.requestedAmountPreLabel"),
      value: formattedCurrencyValue(requestedLoanAmount),
    },
    {
      label: t("remoteIdentification.reasonLabelSMS"),
      value: getReason(),
    },
  ];

  useEffect(() => {
    // Set errors from "outside" (async errors etc).
    if (!isEmpty(formErrors)) {
      onCloseButtonClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formErrors]);

  return (
    <>
      <div className={styles.topBanner}>
        <div className={styles.topBannerItem}>
          <SMSFlow isPending={isPending} isError={isError} />

          <Button
            variant="text"
            onClick={onCloseButtonClick}
            id="modal-cancel-button"
            style={{ minHeight: 0 }}
          >
            {t("bankIDFlow.back")}
          </Button>
        </div>

        <hr className="mx-n6" />

        <div
          className={classnames(
            styles.topBannerItem,
            "flex-column flex-sm-row",
          )}
        >
          <ClipboardCopyLink
            link={remoteIdentificationURL}
            text={t("remoteIdentification.copyShareableLink")}
          />

          <span className={styles.linkExpires}>
            {expiryDateStr
              ? t("remoteIdentification.linkExpires", {
                  expiryDate: expiryDateStr,
                })
              : null}
          </span>
        </div>

        <Text size="md" className={styles.customerViewBadge}>
          {t("remoteIdentification.customerView")}
        </Text>
      </div>

      <DialogTitle title={modalTitle} className={styles.title} />
      <DialogContent className={styles.content}>
        <Toast
          innerText={{
            subtitle: `${t("remoteIdentification.toastText")} ${organisationName}`,
          }}
          className={styles.toastClass}
          disabled
        />

        {content.map(({ label, value }, idx) => {
          if (!value) {
            return null;
          }

          return (
            <div key={idx} className={styles.fieldWrapper}>
              <Text size="sm" className={styles.fieldLabel}>
                {label}
              </Text>
              <Text size="sm">{value}</Text>
            </div>
          );
        })}
      </DialogContent>
      <DialogActions className={styles.actions}>
        <Button
          block
          disabled={true}
          justify="center"
          id="bankid-flow-mobile"
          {...(isSweden && { startIcon: <SVG src={BankIdSVG} /> })}
        >
          {isSweden ? t("login.bankIdGeneric") : t("login.startBankId")}
        </Button>
      </DialogActions>
      <Text size="sm" className={styles.footer}>
        <Trans i18nKey="applicationForm.commons.tosInfo">
          Placeholder: Genom att identifiera dig godkänner du&nbsp;
          <span className={styles.termsAndConditions}>
            Placeholder: användarvillkoren
          </span>
        </Trans>
      </Text>
    </>
  );
};

export default withTranslation()(RemoteIdentificationModalSMS);
